/**
 * Юридична примітка (авторство + застереження про фан-переклади).
 *
 * ЩОБ ЗМІНИТИ ТЕКСТ — правте public/legal.json (перезбірка клієнта не потрібна, достатньо
 * оновити файл на сервері). У рядку "copyright" `{year}` підставляється поточним роком;
 * якщо потрібен фіксований рік — напишіть його замість `{year}`.
 *
 * Кому: лише гостям і користувачам subs_users. Власник/користувачі users.json (роль 'user'),
 * які мають доступ до всіх джерел, примітки не бачать нікуди.
 *
 * Де показується: сторінки «Більше» (і форма входу на ній), каталог для гостей (browse)
 * та сторінка твору smakolykytl — під списком розділів (див. main.ts і pages/novel.ts).
 */
import { getCurrentRole } from './auth';
import { ROLE_USER } from './roles';

interface LegalNotice {
  copyright?: string;
  text?: string;
}

/** Дефіси → нерозривні (U+2011): «фан-перекладами», «2023-2026» не переносяться по рядках. */
const nb = (t: string): string => t.replace(/-/g, '\u2011');

let noticePromise: Promise<LegalNotice | null> | null = null;

function loadNotice(): Promise<LegalNotice | null> {
  if (!noticePromise) {
    noticePromise = fetch('/legal.json', { cache: 'no-cache' })
      .then((r) => (r.ok ? (r.json() as Promise<LegalNotice>) : null))
      .catch(() => null);
  }
  return noticePromise;
}

/** Будує блок примітки; повертає null, якщо файл недоступний або порожній. */
export async function buildLegalNotice(): Promise<HTMLElement | null> {
  const n = await loadNotice();
  if (!n || (!n.copyright && !n.text)) return null;
  const box = document.createElement('footer');
  box.className = 'legal-note';
  if (n.copyright) {
    const c = document.createElement('div');
    c.className = 'legal-copy';
    c.textContent = nb(n.copyright.replace('{year}', String(new Date().getFullYear())));
    box.appendChild(c);
  }
  if (n.text) {
    const p = document.createElement('p');
    p.textContent = nb(n.text);
    box.appendChild(p);
  }
  return box;
}

/** Додає примітку в кінець контейнера (або одразу після `after`, якщо вказано). */
export async function mountLegalNotice(parent: HTMLElement, after?: HTMLElement): Promise<void> {
  // Роль 'user' (users.json) — примітки немає; гість (null) і tier_1 — є.
  if ((await getCurrentRole().catch(() => null)) === ROLE_USER) return;
  const box = await buildLegalNotice();
  if (!box) return;
  if (after && after.parentElement === parent) after.insertAdjacentElement('afterend', box);
  else parent.appendChild(box);
}

/**
 * Те саме, але примітка зʼявляється лише ПІСЛЯ того, як у списку (твори чи розділи) зʼявився
 * справжній вміст: заглушки «Завантаження…», «Пошук…», «Нічого не знайдено» та помилки не рахуються.
 * Тож примітка не може бути видна до завантаження списку.
 */
export function mountLegalNoticeWhenLoaded(listEl: HTMLElement, parent: HTMLElement, after?: HTMLElement): void {
  const hasContent = () =>
    Array.from(listEl.children).some((c) => !c.matches('.empty, .chapters-loading, #grid-loading'));
  let note: HTMLElement | null = null;
  let mounting = false;
  // Слідкуємо постійно: список може очиститися (перемикання «Останні оновлення» / «Усі твори»,
  // новий пошук) — тоді примітку знову ховаємо до появи нового вмісту.
  const sync = async () => {
    if (!hasContent()) {
      if (note) note.hidden = true;
      return;
    }
    if (note) {
      note.hidden = false;
      return;
    }
    if (mounting) return;
    mounting = true;
    await mountLegalNotice(parent, after);
    note = parent.querySelector<HTMLElement>(':scope > .legal-note');
    mounting = false;
    if (note && !hasContent()) note.hidden = true;
  };
  new MutationObserver(() => void sync()).observe(listEl, { childList: true });
  void sync();
}
